import querystring from 'query-string';
import logger from '@ibibo/logger';
import {hasItem, getter, removeItem} from '@ibibo/client-side-storage';
import fetch from './fetch';
import {getBaseCBHeaders, deleteCookie, setCookie, getCookie, getExpire} from './apiConfig';
import {v4 as uuidv4} from 'uuid';

const crId = uuidv4();
/**
 * Objective of this module is to provide following features:
 * - [x] Simple wrapper for making network requests
 * - [ ] Latency Tracking
 * - [ ] Auto push GA analytics related to API endpoints
 * - [ ] Cancel if existing request over the air
 * - [ ] Retry as per specified count
 * - [ ] Short-Circuit request as per defined time
 * - [ ] Priority fetching
 * - [ ] No network response
 * - [ ] Logging utility logger/index.ts or logger.server.ts
 */

// https://developer.mozilla.org/en-US/docs/Web/API/AbortController/abort
// can implement fetch cancel request via AbortController, but require polyfill, AbortController also supported under node-fetch

export type xhrType = 'GET' | 'POST';

interface IXhrHelper<RequestDataGeneric> {
  name: string;
  type: xhrType;
  url: string;
  headers?: object;
  requestData?: object | string;
  credentials?: string;
}

export default async function megatron<RequestDataGeneric, ResponseDataGeneric>({
  name,
  headers,
  type,
  url,
  requestData,
  credentials,
}: IXhrHelper<RequestDataGeneric>): Promise<ResponseDataGeneric> {
  let params = requestData;

  const getExperimentKey = () => {
    let exp = {};
    if (window.experiment) {
      exp = {experiment: window.experiment.value};
    }
    if (hasItem('experiment')) {
      exp = getter('experiment');
    }
    return exp;
  };

  const getParam = paramKey => {
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });

    return params[paramKey];
  };

  const getCrid = () => {
    if (getParam('crId')) {
      return getParam('crId');
    } else {
      const crId = uuidv4();
      const newUrl = `${window.location.href}${window.location.search ? '&' : '?'}crId=${crId}`;
      window.history.replaceState({}, '', newUrl);
      return crId;
    }
  };

  const ccdeCookie = getCookie('ccde');

  const countryHeadersIndia = {
    language: 'eng',
    currency: 'INR',
    region: 'IN',
  };

  const countryHeadersAe = countryHeadersIndia;

  const countryHeaders = {};

  const newHeaders = getBaseCBHeaders();

  const urlList = url.split('.goibibo.com');

  const mmtAuth = getCookie('mmt-auth');
  if (!mmtAuth) {
    setCookie(
      'mmt-auth',
      window.__initial_state__?.myaccount?.authToken,
      getExpire(),
      'makemytrip.com',
    );
  }

  const location = window.location.pathname;
  if (!location.startsWith('/agent')) {
    countryHeaders['header'] = ccdeCookie === 'AE' ? countryHeadersAe : countryHeadersIndia;
  }

  if (location.startsWith('/agent/transactionreport')) {
    countryHeaders['header'] = ccdeCookie === 'AE' ? countryHeadersAe : countryHeadersIndia;
  }

  let apiHeaders = {
    ...(urlList.length >= 2 ? {} : newHeaders),
    ...headers,
    crId: getCrid(),
    ...(countryHeaders?.header || {}),
    ...getExperimentKey(),
    ...(window.__initial_state__?.myaccount?.authToken && {
      'mmt-auth': window.__initial_state__?.myaccount?.authToken,
    }),
  };
  if (apiHeaders['experiment']) {
    if (window.location.pathname.startsWith('/hotels/')) {
      if (window.location.pathname.startsWith('/hotels/v1')) {
        apiHeaders.experiment = '0';
      } else {
        apiHeaders.experiment = '1';
      }
    }
  }

  var options = {
    headers: apiHeaders,
    credentials: credentials || 'include',
    method: type,
  };

  if (type === 'POST') {
    options.body = params;
  }
  const request = new Request(url);
  const rawResponse = await fetch(request, options);
  if (rawResponse.status === 401) {
    const url = `${__ENV_CONFIG__.MYPARTNER}/agents/logout/?bheem=true`;
    try {
      await fetch(url);
    } catch (err) {
      deleteCookie('sessionid');
      deleteCookie('mmt-auth');
    }
    window.open('/agent/login', '_self');
    deleteCookie('sessionid');
    deleteCookie('mmt-auth');
    if (hasItem('myPartnerAuth')) {
      removeItem('myPartnerAuth');
    }
    if (hasItem('experiment')) {
      removeItem('experiment');
    }
  }
  const respJson = await rawResponse.json();
  return respJson;
}

export async function get(url, {name, logLevel = 'verbose', options = {}}) {
  try {
    const response = await fetch(url, options);
    const responseJson = await response.json();
    // logger[logLevel]({
    //   type: '@ibibo/megatron',
    //   message: 'api response',
    //   data: {
    //     name,
    //     url,
    //     data: responseJson,
    //   },
    // });
    return {data: responseJson};
  } catch (err) {
    logger.error({
      type: '@ibibo/megatron',
      message: 'get call failed',
      data: {
        name,
        url,
      },
      error: err,
    });
    return {data: undefined};
  }
}
